export const en = {
  // App
  appName: "DevPulse",

  // Navigation
  navDashboard: "Dashboard",
  navContributors: "Contributors",
  navTeams: "Teams",
  navRepositories: "Repositories",

  // Views
  viewOrganization: "Organization",
  viewGroup: "Group",
  viewIndividual: "Individual",
  viewSelectPlaceholder: "Select View",

  // Common
  loading: "Loading data...",
  authenticating: "Authenticating...",
  loadingError: "Data Loading Error",
  authError: "Authentication Error",
  retry: "Retry",
  tryAgain: "Try Again",
  profile: "Profile",
  settings: "Settings",
  logout: "Logout",
  selectTimeRange: "Select time range",
  selectTeam: "Select a team...",
  selectDeveloper: "Select a developer...",
  normalizedView: "Normalized",
  absoluteView: "Absolute",

  // Time ranges
  timeLast7Days: "Last 7 days",
  timeLast14Days: "Last 2 weeks",
  timeLast28Days: "Last 4 weeks",
  timeLast8Weeks: "Last 8 weeks",
  timeLast12Weeks: "Last 12 weeks",
  timeLast30Days: "Last 30 days",
  timeAll: "All",

  // Dashboard titles
  dashboardOrganizationTitle: "Organization Dashboard",
  dashboardOrganizationSubtitle:
    "Overview of development metrics across all repositories and team members",
  dashboardTeamTitle: "Team Dashboard",
  dashboardTeamSubtitle: "Development metrics for {team} team members",
  dashboardTeamSubtitleGeneric: "Development metrics for team members",
  dashboardTeamNoData: "No teams configured. Please add team data to view team-specific metrics.",
  dashboardIndividualTitle: "Individual Dashboard",
  dashboardIndividualSubtitle: "Development metrics for {author}",
  dashboardIndividualSubtitleGeneric: "Development metrics for individual contributors",
  dashboardIndividualNoData: "No authors found. Please load data to view individual metrics.",

  // Contributors page
  contributorsTitle: "Contributors",
  contributorsSubtitle: "Analyze contributor performance and activity",
  contributorsNoData: "No contributor data available for the selected time range.",

  // Teams page
  teamsTitle: "Teams",
  teamsSubtitle: "Analyze team performance and activity",
  teamsNoData: "No team data available for the selected time range.",

  // Repositories page
  repositoriesTitle: "Repositories",
  repositoriesSubtitle: "Analyze repository performance and activity",
  repositoriesNoData: "No repository data available for the selected time range.",
  repositoriesScoreTitle: "Repository Commit Complexity Score",
  repositoriesScoreDescription: "Total complexity score of all commits per repository.",
  repositoriesPRsTitle: "Repository Pull Requests",
  repositoriesPRsDescription: "Number of merged pull requests per repository.",

  // DORA Metrics page
  doraMetricsTitle: "DORA Metrics",
  doraMetricsSubtitle:
    "DevOps Research and Assessment (DORA) metrics for measuring software delivery performance",
  doraMetricsSubtitleTeam: "DORA metrics for {team} team members",
  doraMetricsSubtitleIndividual: "DORA metrics for {author}",
  doraNoDataTitle: "No DORA Metrics Data Available",
  doraNoDataDescription:
    "DORA metrics require releases, pull requests, and commit data. Please ensure data is collected and exported.",
  doraMissingDataTitle: "Missing Data for Complete DORA Metrics",
  doraMissingDataDescription:
    "The following data points are missing or incomplete for full DORA metrics calculation:",
  doraDataLimitationsTitle: "Data Limitations",
  doraDataLimitationsDescription:
    "The DORA metrics shown are calculated from available data, which has some limitations:",
  doraOverallPerformance: "Overall DORA Performance Level",
  doraOverallDescription:
    "Based on the four key metrics, your team's overall performance level is calculated according to DORA benchmarks.",
  doraDeploymentFrequency: "Deployment Frequency",
  doraLeadTime: "Lead Time for Changes",
  doraChangeFailureRate: "Change Failure Rate",
  doraTimeToRestore: "Time to Restore Service",
  doraPerMonth: "per month",
  doraPerWeek: "per week",
  doraDays: "days",
  doraHours: "hours",
  doraDeploymentsByMonth: "Deployments by Month",
  doraDeploymentsByWeek: "Deployments by Week",
  doraLeadTimeTrend: "Lead Time Trend",
  doraFailureRateTrend: "Failure Rate Trend",
  doraRestoreTimeTrend: "Restore Time Trend",
  doraDeploymentFrequencyDetail: "Deployment Frequency Over Time",
  doraDeploymentFrequencyDetailDescription: "Number of deployments per period",
  doraLeadTimeDetail: "Lead Time for Changes Over Time",
  doraLeadTimeDetailDescription: "Average time from PR creation to merge (in hours) per period",
  doraPeriodWeekly: "Weekly",
  doraPeriodMonthly: "Monthly",
  doraPeriodSelectLabel: "Period",
  doraMethodologyTitle: "Methodology & Data Sources",
  doraDeploymentFrequencyMethodology:
    "Calculated from published releases (excluding drafts and pre-releases) in the releases database table.",
  doraLeadTimeMethodology:
    "Calculated as the time between pull request creation and merge, measured from the pull_requests table.",
  doraChangeFailureRateMethodology:
    "Estimated by detecting bugfix or revert commits within 7 days after a release. This is an approximation since we don't have explicit deployment failure tracking.",
  doraTimeToRestoreMethodology:
    "Estimated as the time between a release and the first bugfix/revert commit. This is an approximation since we don't have incident management data.",

  // DORA Performance Levels
  doraLevelElite: "ELITE",
  doraLevelHigh: "HIGH",
  doraLevelMedium: "MEDIUM",
  doraLevelLow: "LOW",

  // DORA Missing Data Points
  doraMissingReleases: "Releases data for deployment frequency",
  doraMissingPullRequests: "Pull Request data for lead time calculation",
  doraMissingCommits: "Commit data for failure analysis",
  doraMissingDeployments: "Deployment data (Phase 2) - using releases as proxy",
  doraMissingWorkflowRuns: "CI/CD workflow data (Phase 2) - limited pipeline visibility",
  doraMissingIncidents: "Incident tracking data (Phase 3) - using bugfix commits as proxy",

  // DORA Partial Metrics Notice
  doraPartialMetricsTitle: "Partial DORA Metrics",
  doraPartialMetricsDescription:
    "These metrics are calculated using available data (releases, pull requests, and commits). For more accurate metrics, GitHub Actions workflow runs and incident tracking data are needed.",
  doraEstimatedBadge: "Estimated",
  doraAccurateBadge: "Accurate",
  doraDataQualityEstimated: "Using proxy data for calculation",
  doraDataQualityAccurate: "Using actual deployment and incident data",
  doraNoDataAvailable: "No data available for this metric",

  error: "Error",

  // Tabs
  tabPerformance: "Performance",
  tabScore: "Score",
  tabPRs: "PRs",
  tabIssues: "Issues",
  tabActivity: "Activity",
  tabReleases: "Releases",
  tabStaffing: "Staffing",

  // Staffing tab
  staffingTitle: "Team Staffing Overview",
  staffingDescription: "Number of team members per team, sorted by team size.",
  staffingMemberCount: "{count} members",
  staffingMember: "{count} member",

  // Activity
  recentActivity: "Recent Activity",

  // Releases tab
  releasesTitle: "Releases (Last 12 Weeks)",
  releasesDescription: "Total number of releases published across all repositories",
  releasesChartTitle: "Releases by Repository (Last 6 Weeks)",
  releasesChartDescription: "Weekly release activity per repository",

  // Performance tab
  performanceScoreTitle: "Overall Performance Score (0-100)",
  performanceScoreDescription:
    "A comprehensive metric combining code quality (30%), delivery throughput (25%), code impact (20%), problem-solving (15%), and work versatility (10%).",
  performanceScoreAverage: "Average: {value}",
  performanceScoreComparison:
    " — The percentage shows how each contributor compares to this average.",

  performanceScoreTeamTitle: "Overall Team Performance Score (0-100)",
  performanceScoreTeamDescription:
    "Aggregated performance score combining all team members' contributions across code quality, delivery throughput, code impact, problem-solving, and work versatility.",

  // Score tab
  scoreTitle: "Commit Complexity Score",
  scoreDescription:
    "Measures code complexity based on files changed, lines modified, function changes, and cyclomatic complexity. Higher scores indicate more challenging and impactful contributions.",
  scoreAverage: "Average: {value} points",
  scoreComparison: " — The percentage shows how each contributor compares to this average.",

  scoreTeamTitle: "Team Commit Complexity Score",
  scoreTeamDescription:
    "Combined complexity score from all team members' commits, measuring technical challenge and impact of code changes. Higher scores indicate more complex and impactful work.",

  // PRs tab
  prsTitle: "Merged Pull Requests",
  prsDescription:
    "Total number of successfully merged pull requests. This metric reflects code review participation, collaboration, and successful delivery of features and fixes.",
  prsAverage: "Average: {value} PRs",
  prsComparison: " — The percentage shows how each contributor compares to this average.",

  prsTeamTitle: "Team Merged Pull Requests",
  prsTeamDescription:
    "Total merged pull requests from all team members. This reflects team collaboration, code review participation, and successful delivery of features and fixes.",

  // Performance tab - Insight cards
  insightMergedPRs: "Merged PRs",
  insightPRThroughput: "PR Throughput",
  insightFeatureCommits: "Feature Commits",
  insightFeatures: "Features",
  insightBugfixCommits: "Bugfix Commits",
  insightBugfixes: "Bugfixes",
  insightMaintenanceCommits: "Maintenance Commits",
  insightMaintenance: "Maintenance",

  // Chart titles
  chartCommitsByClassification: "Commits by Classification",
  chartCommitsBySize: "Commits by Size",
  chartRepositoryCommits: "Commits by Repository",
  chartRepositoryCommitsDesc: "Number of commits to each repository in {timeRange}",
  chartRepositoryPRsAndCommits: "PRs and Commits by Repository",
  chartRepositoryPRsAndCommitsDesc:
    "Number of pull requests and commits to each repository in {timeRange}",
  chartCommitScoresByWeek: "Commit Scores by Week",
  chartCommitScoresByWeekDesc: "Total commit complexity scores grouped by week and stacked by size",
  chartCommitScoresByClassificationWeek: "Commit Scores by Classification",
  chartCommitScoresByClassificationWeekDesc:
    "Total commit complexity scores grouped by week and stacked by classification",
  chartPRsMerged: "PRs Merged This Month",
  chartPRsMergedByWeek: "PRs Merged by Week",
  chartPRsMergedByWeekDesc: "Number of merged pull requests per week for the last 6 weeks",
  chartCodeChanges: "Code Changes",
  chartCodeChangesByWeek: "Code Changes by Week",
  chartCodeChangesByWeekDesc: "Lines added and removed from merged PRs per week",
  chartIssuesCreated: "Issues Created This Month",
  chartIssuesClosed: "Issues Closed This Month",
  chartActivityTitle: "Contribution Activity",
  chartActivityDescription:
    "Organization-wide activity including commits, pull requests, and issues over the last year",
  chartDetails: "Details",

  // Chart labels
  linesAdded: "Lines Added",
  linesRemoved: "Lines Removed",

  // Language selector
  languageLabel: "Language",
  languageEnglish: "English",
  languageGerman: "Deutsch",
  languageSpanish: "Español",
  languageChinese: "中文",

  // DataTable
  dataTableFilters: "Filters",
  dataTableFilterBy: "Filter by:",
  dataTableNoResults: "No results.",
  dataTableRowsPerPage: "Rows per page:",
  dataTablePage: "Page",
  dataTableOf: "of",
  dataTableTotalRows: "total rows",
  dataTableSearch: "Search...",

  // Charts
  chartTrend: "Trend",
  chartDistribution: "Distribution",
  chartDate: "Date",

  // Quick insights
  quickInsightDate: "Last 7 days",
  quickInsightVsLastWeek: "vs {count} last week",
  quickInsightVsLast7Days: "vs {count} previous 7 days",
  quickInsightVsLast14Days: "vs {count} previous 14 days",
  quickInsightVsLast28Days: "vs {count} previous 28 days",
  quickInsightVsLast8Weeks: "vs {count} previous 8 weeks",
  quickInsightVsLast12Weeks: "vs {count} previous 12 weeks",
  quickInsightVsLastMonth: "vs {count} last month",

  // Data table column headers
  tableColumnTitle: "Title",
  tableColumnMessage: "Message",
  tableColumnAuthor: "Author",
  tableColumnDate: "Date",
  tableColumnWeekday: "Weekday",
  tableColumnCreated: "Created",
  tableColumnScore: "Score",
  tableColumnFiles: "Files",
  tableColumnAdditions: "+",
  tableColumnDeletions: "-",
  tableColumnType: "Type",
  tableColumnTeam: "Team",
  tableColumnContributor: "Contributor",
  tableColumnValue: "Value",
  tableColumnCount: "Count",

  // Chart descriptions
  chartCommitsByClassificationDesc:
    "Number of commits grouped by classification (commit type) and stacked by complexity size",
  chartCommitsBySizeDesc:
    "Number of commits grouped by complexity size and stacked by classification (commit type)",
  chartCodeChangesDesc: "Lines added and removed from merged PRs",
  chartPRsMergedDesc: "{count} PRs merged (vs {prevCount} previous period)",
  chartIssuesCreatedDesc: "{count} issues created (vs {prevCount} previous period)",
  chartIssuesClosedDesc: "{count} issues closed (vs {prevCount} previous period)",

  // Top performers tables
  topTeamsTitle: "Top Teams",
  topContributorsTitle: "Top Contributors",
  topContributorsTeamMembersLabel: "Team Members",
  topContributorsExternalLabel: "External Contributors",
  topTeamRepositoriesTitle: "Top Repositories",
  topTeamRepositoriesDescription: "Repositories the team contributes to the most",

  // Common labels
  commits: "Commits",
  pullRequests: "Pull Requests",

  // Settings page
  settingsTitle: "Settings",
  settingsSubtitle: "Manage application settings and preferences",
  settingsClassificationTitle: "Classification",
  settingsClassificationDescription:
    "Select which commit classifications to include in charts and metrics. Changes apply to all dashboard views.",
  settingsTeamsTitle: "Teams",
  settingsTeamsDescription:
    "Select which teams to display across the application. Unselected teams will not appear in any view.",
  settingsSelectAll: "Select All",
  settingsDeselectAll: "Deselect All",
  settingsNoTeams: "No teams available. Teams will appear here once data is loaded.",

  // Classification types
  classificationFeature: "Feature",
  classificationBugfix: "Bugfix",
  classificationRefactoring: "Refactoring",
  classificationCi: "CI/CD",
  classificationTests: "Tests",
  classificationDocs: "Documentation",
  classificationChore: "Chore",
  classificationPerf: "Performance",
  classificationRevert: "Revert",
  classificationMerge: "Merge",
  classificationArchive: "Archive",
  classificationStyle: "Style",
  classificationOther: "Other",
  classificationUnclassified: "Unclassified",

  // Search
  searchTeams: "Search teams...",
  searchContributors: "Search contributors...",
  searchRepositories: "Search repositories...",
  searchCommits: "Search commits...",
  noSearchResults: "No results found for your search.",

  // Commits table
  commitsTableTitle: "All Commits",
  commitsTableDescription: "Detailed view of all commits in the selected time range",
  tableColumnRepo: "Repo",
  tableColumnClassification: "Classification",

  // Performance explanation
  performanceExplanationTitle: "Performance Score Explanation",
  performanceExplanationSubtitle:
    "Understanding how the performance score is calculated and what it means",
  performanceMethodologyTitle: "Calculation Methodology",
  performanceMethodologyDescription:
    "The performance score is a comprehensive metric (0-100) that combines multiple dimensions of developer contribution. Each component is calculated independently and normalized to a 0-100 scale before being weighted:",
  performanceQualityWeight: "Code Quality (30%)",
  performanceQualityDesc:
    "Based on commit complexity scores (simple, medium, complex). Higher complexity scores indicate more challenging contributions. When all commits are simple, this may reach 100 as that's the max for that activity level.",
  performanceThroughputWeight: "Delivery Throughput (25%)",
  performanceThroughputDesc:
    "Measured by merged PRs and commit frequency normalized by time period. Reflects delivery speed. Can reach 100 when activity is very high relative to the time period.",
  performanceImpactWeight: "Code Impact (20%)",
  performanceImpactDesc:
    "Based on total lines changed (additions + deletions) normalized by time period. Can reach 100 with very high code churn. Zero if no code changes.",
  performanceProblemSolvingWeight: "Problem Solving (15%)",
  performanceProblemSolvingDesc:
    "Based on issues resolved normalized by time period. Will be 0 if no issues were closed during the period.",
  performanceVersatilityWeight: "Work Versatility (10%)",
  performanceVersatilityDesc:
    "Based on diversity of commit types (features, bugfixes, maintenance, etc.). Higher when work spans multiple types. Can be 0-100 based on commit type distribution.",
  performanceScoreNote:
    "Note: Components reaching 100 means maximum performance for that dimension in the given time period. Zero values indicate no activity in that dimension. The overall score is the weighted average of all components.",
  performanceDailyValuesTitle: "Daily Performance Values",
  performanceDailyValuesDescription:
    "Daily breakdown of performance scores showing how the score evolved over time",
  performanceReasoningTooltip:
    "This score is calculated based on the day's contribution metrics including commits, PRs, code changes, and issues resolved",
  tableColumnReasoning: "Reasoning",

  // Normalized view tooltip
  normalizedViewTooltip:
    "Toggle between absolute values and values normalized per contributor. Normalized view shows average contribution per team member or repository contributor.",
  normalizedViewLabel: "View Mode",
}

export type TranslationKeys = typeof en
